import React, { useEffect, useState } from 'react';
import './AnalyzeLoader.scss';
import { t } from 'i18next';

export interface AnalyzeStage {
  title: string; // i18n key (may contain a {{provider}} placeholder)
  subtitle: string; // i18n key
  titleParams?: Record<string, string>;
}

const DEFAULT_STAGE_MS = 1000;

// Default sequence — domain visibility check across three providers.
const DEFAULT_STAGES: AnalyzeStage[] = [
  {
    title: 'analyzeVisibilityTitle',
    titleParams: { provider: 'ChatGPT' },
    subtitle: 'analyzeSubtitleChatgpt',
  },
  {
    title: 'analyzeVisibilityTitle',
    titleParams: { provider: 'Gemini' },
    subtitle: 'analyzeSubtitleGemini',
  },
  {
    title: 'analyzeVisibilityTitle',
    titleParams: { provider: 'Perplexity' },
    subtitle: 'analyzeSubtitlePerplexity',
  },
];

export const ANALYZE_LOADER_TOTAL_MS = DEFAULT_STAGES.length * DEFAULT_STAGE_MS;

// The project-setup loader plays a paced step timeline (3s + 3s + 14s for the first three steps),
// then wraps up: once real data has landed we redirect after a short tail (→ READY), otherwise we
// hold until the hard cap (MAX). Data is polled every POLL.
// Steps 1-3 = 20s, + a 4s tail (steps 4+5 at 2s each) once data is ready → 24s. Full tail (up to 15s
// each) tops out at 50s, held by the 52s cap.
export const PROJECT_SETUP_READY_MS = 24_000;
export const PROJECT_SETUP_MAX_MS = 52_000;
export const PROJECT_SETUP_POLL_MS = 3_000;
// Brief beat held at 100% so the bar visibly completes before the loader is unmounted.
export const BAR_FINISH_MS = 500;

interface AnalyzeLoaderProps {
  stages?: AnalyzeStage[];
  hint?: string; // i18n key
  stageMs?: number;
  onExit?: () => void; // when set, reserves + renders an escape action (e.g. "Back to projects")
  exitLabel?: string; // i18n key for the escape action
  exitVisible?: boolean; // reveal the (already-reserved) escape action without a layout jump
  completed?: boolean; // fill the bar to 100% — set only when the real work is done, never on a timer
}

// Full-page "analyzing your visibility" loader. Cycles through stages while the bar fills forward.
const AnalyzeLoader: React.FC<AnalyzeLoaderProps> = ({
  stages = DEFAULT_STAGES,
  hint = 'analyzeVisibilityHint',
  stageMs = DEFAULT_STAGE_MS,
  onExit,
  exitLabel = 'backToProjects',
  exitVisible = false,
  completed = false,
}) => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((s) => s + 1), stageMs);
    return () => clearInterval(id);
  }, [stageMs]);

  // Messages loop so the screen stays alive while we wait for real data (an unknown, variable time).
  const current = stages[tick % stages.length];
  // Bar eases to ~90% over one pass and HOLDS; it only reaches 100% when `completed` — i.e. the real
  // work is done and the view is about to load — never on a timer alone.
  const progress = completed ? 100 : Math.min(90, Math.round(((tick + 1) / stages.length) * 90));

  return (
    <div className="analyze-loader">
      <div className="analyze-loader__belt">
        <div className="logo-box" />
      </div>
      <div className="analyze-loader__center">
        {/* Models revolve around the ChatBeat mark while breathing in and out; the mark stays put. */}
        <div className="analyze-loader__orbit">
          <div className="analyze-loader__orbit-ring">
            <span className="analyze-loader__orbit-logo analyze-loader__orbit-logo--n">
              <span className="analyze-loader__orbit-logo-move">
                <span className="analyze-loader__orbit-logo-spin">
                  <span className="ico ico--gpt-bck ico--md" />
                </span>
              </span>
            </span>
            <span className="analyze-loader__orbit-logo analyze-loader__orbit-logo--e">
              <span className="analyze-loader__orbit-logo-move">
                <span className="analyze-loader__orbit-logo-spin">
                  <span className="ico ico--gemini-bck ico--md" />
                </span>
              </span>
            </span>
            <span className="analyze-loader__orbit-logo analyze-loader__orbit-logo--s">
              <span className="analyze-loader__orbit-logo-move">
                <span className="analyze-loader__orbit-logo-spin">
                  <span className="ico ico--perplexity-bck ico--md" />
                </span>
              </span>
            </span>
            <span className="analyze-loader__orbit-logo analyze-loader__orbit-logo--w">
              <span className="analyze-loader__orbit-logo-move">
                <span className="analyze-loader__orbit-logo-spin">
                  <span className="ico ico--claude-bck ico--md" />
                </span>
              </span>
            </span>
          </div>
          <span className="analyze-loader__orbit-core" />
        </div>
        <h2 className="analyze-loader__title">{t(current.title, current.titleParams)}</h2>
        <p className="analyze-loader__subtitle">{t(current.subtitle)}</p>
        <div className="analyze-loader__bar">
          <span className="analyze-loader__bar-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="analyze-loader__hint">{t(hint)}</div>
        {onExit && (
          // Always rendered (reserves its space) so revealing it later can't shift the layout.
          <button
            type="button"
            className={`analyze-loader__exit${exitVisible ? '' : ' analyze-loader__exit--hidden'}`}
            onClick={onExit}
            tabIndex={exitVisible ? 0 : -1}
            aria-hidden={!exitVisible}
          >
            {t(exitLabel)}
          </button>
        )}
      </div>
    </div>
  );
};

export default AnalyzeLoader;
